import { UserClaims } from '@okta/okta-auth-js';
import { IAutoQuote, IPolicyDocument, IUser } from '@sureapp/platform-sdk';
import { analytics } from '@sureapp/wl-toggle-common/dist/utils';
import isBoolean from 'lodash/isBoolean';
import omit from 'lodash/omit';

export function trackChanges(name: string, description: any) {
  analytics.track(name, description);
}

/**
 * Track changes to variable selections appropriately in analytics.
 * Casts keys and values to GA-friendly events.
 *
 * @param changesObj Key/Value mapping of new variable settings.
 */
export function trackVariablesChanges(changes: Record<string, unknown>) {
  Object.entries(changes).forEach(([key, value]) => {
    analytics.track('Quote Variable Adjusted', {
      category: 'Quotes',
      label: key,
      value: isBoolean(value) ? Number(value) : value,
      key,
      updated_value: value,
    });
  });
}

/**
 * A wrapper around the Segment call that also notifies other services that care
 * about who the current user is.
 */
export const identifyUserToAllServices = (
  oktaClaims?: Partial<UserClaims>,
  user?: IUser
) => {
  analytics.identifyUser(oktaClaims as any, user);
};

type TQuoteAssistBaseAnalyticsProperties = {
  agent_id: string | undefined;
  lob: string;
  quote_number: string | undefined;
};

/**
 * Builds an object with base properties to be sent with Quote Assist Segment events
 *
 * @param quote IAutoQuote
 * @returns TQuoteAssistBaseAnalyticsProperties
 */
export const getQuoteAssistBaseAnalyticsProperties = (
  quote?: IAutoQuote | null,
  keysToOmit?: Array<keyof TQuoteAssistBaseAnalyticsProperties>
): Partial<TQuoteAssistBaseAnalyticsProperties> =>
  omit(
    {
      agent_id: quote?.application?.metadata?.agent_id ?? undefined,
      lob: 'Auto',
      quote_number: quote?.quote_number ?? undefined,
    },
    keysToOmit ?? []
  );

/**
 * Tracks when a user views a document
 *
 * @param doc Pick<IPolicyDocument, 'name' | 'document_type'>
 */
export function trackDocumentView(
  doc: Pick<IPolicyDocument, 'name' | 'document_type'>
) {
  analytics.track('Document Viewed', {
    document_name: doc.name,
    category: doc.document_type,
  });
}

/**
 * Utility to ensure we never send a payment_amount thats not a number
 * we are defaulting this to 0 if somehow we pass a non-parseable number
 * to this tracking function.
 * @param paymentAmount - the payment amount passed in from the FE functions
 * @returns number
 */
const coercePaymentAmount = (paymentAmount?: number | string) => {
  let amount: number;
  if (typeof paymentAmount === 'string') {
    // for the offchance the FE is passing in currency format strings
    const floatParseableString = paymentAmount.replace('$', '');
    const parsedAmount = parseFloat(floatParseableString);
    amount = Number.isNaN(parsedAmount) ? 0 : parsedAmount;
  } else if (paymentAmount === undefined) {
    amount = 0;
  } else {
    amount = paymentAmount;
  }
  return amount;
};

/**
 * Tracks the submission of a payment
 *
 * @param errorCode string
 */
export const trackSubmitPayment = ({
  paymentAmount,
  paymentMethodId,
  policyNumber,
  quoteNumber,
  errorCode,
}: {
  paymentAmount?: number | string;
  paymentMethodId?: string;
  policyNumber?: string | null;
  quoteNumber?: string;
  errorCode?: string;
}) => {
  trackChanges('Payment Submitted', {
    field_error: errorCode || '',
    payment_amount: coercePaymentAmount(paymentAmount),
    payment_method: paymentMethodId,
    policy_number: policyNumber,
    quote_number: quoteNumber,
  });
};
